/**
 * Fábrica de Vídeos — regras da tela, sem React nem MUI.
 *
 * O QUE ESTA TELA É (v2, decisão da Mariana em 24/09/2026): a tela GERA o vídeo dentro do BI pela
 * API paga do Higgsfield (serviço services/fabrica-videos, porta 8105) e mostra os créditos e o
 * preço ANTES de cada etapa. O conector do Claude não serve a um servidor (é OAuth de navegador),
 * então a v1 (tela monta o briefing e a Mariana cola no Claude) fica só como caminho alternativo:
 * `montarPedido` continua aqui para o botão "Prefiro gerar no Claude".
 *
 * Método (manual "Fábrica de Vídeos", UP DIGITAL, v1 de 23/09/2026):
 *   um vídeo vertical 9:16 de ~20 s = 4 módulos de 5 s (hook → característica → close de
 *   detalhes → CTA), apresentadora ultrarrealista falando PT-BR com lip-sync, produto igual ao
 *   anúncio (a foto real do anúncio é a referência da cena), legenda karaokê com o roteiro.
 *
 * Os números de custo REAIS vêm do serviço (endpoint /estimate da API + PTAX do BCB). As
 * constantes abaixo (créditos do plano, preço do plano) são só da estimativa do caminho
 * alternativo pelo Claude.
 */

import type { RoteiroInsumos } from '../../api/bridge'

// ---------------------------------------------------------------------------------------------
// Constantes do manual
// ---------------------------------------------------------------------------------------------

/** Até 12 palavras cabem em 5 s de fala natural (≈ 2,5 palavras/s). */
export const MAX_PALAVRAS_MODULO = 12
export const PALAVRAS_POR_SEGUNDO = 2.5
export const SEGUNDOS_POR_MODULO = 5

/** Créditos Higgsfield por peça (manual, seção 7). */
export const CREDITOS = {
  /** Nano Banana Pro 2K — imagem da cena base. */
  cena: 2,
  /** Seedance 2.0 1080p std, 5 s, com áudio. */
  modulo: 45,
  /** FFmpeg no sandbox do Higgsfield. */
  montagem: 0,
} as const

/** Planos do Higgsfield como o manual cotou (3.000 créditos). US$ por crédito. */
export const PLANOS = {
  ultra_mensal: { label: 'Ultra mensal (US$ 129 / 3.000 créditos)', usdPorCredito: 129 / 3000 },
  ultra_anual: { label: 'Ultra anual (US$ 99 / 3.000 créditos)', usdPorCredito: 99 / 3000 },
} as const
export type PlanoId = keyof typeof PLANOS

/** Cotação usada no manual: PTAX de 22/09/2026. É só o valor inicial do campo; a tela deixa
 *  editar e escreve de onde veio. Não é dado ao vivo. */
export const DOLAR_PADRAO = 5.1161
export const DOLAR_PADRAO_ORIGEM = 'PTAX 22/09/2026, a cotação do manual'
export const IOF_CARTAO_INTERNACIONAL = 0.035

// ---------------------------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------------------------

export type ContaMl = 'FIESTAPARTYFESTA' | 'FIESTAPARTYHOME' | ''

export type ModuloId = 'hook' | 'carac' | 'det' | 'cta'

export type Roteiro = Record<ModuloId, string>

export type Briefing = {
  produto: {
    mlb: string
    titulo: string
    conta: ContaMl
    link: string
    thumbnail: string
  }
  elemento: {
    /** Nome do Elemento no Higgsfield (máx. 32 caracteres, minúsculas com hífen). */
    nome: string
    /** ID do Elemento. Vazio = ainda não existe; o Claude cria a partir da foto do anúncio. */
    id: string
  }
  /** Como a apresentadora chama o produto na fala ("essas taças de champanhe"). */
  chamada: string
  roteiro: Roteiro
  cena: {
    apresentadora: ApresentadoraId
    ambiente: AmbienteId
    /** O que ela mostra no módulo 2 (partes, quantidade, material). */
    partes: string
    /** O que a câmera mostra no close do módulo 3. */
    detalhesVisuais: string
    /** Caixa, kit ou embalagem que fica na mesa no CTA. */
    embalagem: string
    /** Pedido livre de ajuste na cena ("cabelo preso", "mais luz no produto"). */
    ajuste: string
  }
  custo: {
    plano: PlanoId
    dolar: number
    iof: boolean
  }
  /** Resumo dos insumos lidos pelo bridge (pesquisa + anúncio). null = não carregou. */
  insumos: ResumoInsumos | null
  /** id do projeto salvo no serviço (services/fabrica-videos). Vazio = ainda não salvo. */
  projetoId: string
}

export const MODULOS: Array<{
  id: ModuloId
  n: number
  nome: string
  oQueAparece: string
  papel: string
}> = [
  {
    id: 'hook',
    n: 1,
    nome: 'Hook de atenção',
    oQueAparece: 'Olhando para a câmera, ela levanta o produto com surpresa e entusiasmo.',
    papel: 'Tensão ou curiosidade: o motivo para não passar o vídeo.',
  },
  {
    id: 'carac',
    n: 2,
    nome: 'Característica',
    oQueAparece: 'Ela segura e gira o produto na luz, mostrando as partes.',
    papel: 'O que é: quantidade, material, medida.',
  },
  {
    id: 'det',
    n: 3,
    nome: 'Close de detalhes',
    oQueAparece: 'A câmera se aproxima do produto; o rosto dela continua visível, falando.',
    papel: 'O que só se vê de perto: acabamento, brilho, textura.',
  },
  {
    id: 'cta',
    n: 4,
    nome: 'CTA',
    oQueAparece: 'Plano médio, produto ao lado da caixa, ela aponta para a câmera.',
    papel: 'Ação clara: link, loja, hoje.',
  },
]

// ---------------------------------------------------------------------------------------------
// Apresentadora e ambiente — opções fechadas, para o vídeo da loja ter cara de série
// ---------------------------------------------------------------------------------------------

export const APRESENTADORAS = {
  mulher_32: {
    label: 'Mulher ~32 anos, sorriso simpático, blusa clara (padrão do manual)',
    prompt:
      'A Brazilian woman around 32 years old, friendly warm smile, natural makeup, hair loose, wearing a simple light-colored blouse',
  },
  mulher_45: {
    label: 'Mulher ~45 anos, mãe anfitriã, camisa jeans',
    prompt:
      'A Brazilian woman around 45 years old, warm confident smile, natural makeup, shoulder-length hair, wearing a casual denim shirt',
  },
  mulher_25: {
    label: 'Mulher ~25 anos, jovem e animada, camiseta colorida',
    prompt:
      'A Brazilian woman around 25 years old, bright enthusiastic smile, natural makeup, curly hair loose, wearing a colorful casual t-shirt',
  },
} as const
export type ApresentadoraId = keyof typeof APRESENTADORAS

export const AMBIENTES = {
  cozinha: {
    label: 'Cozinha clara, luz da manhã (padrão do manual)',
    prompt:
      'in a bright modern family kitchen at home in the morning, soft sunlight coming through a window, a wooden table, a few plants, cozy lived-in details',
  },
  sala_festa: {
    label: 'Sala de estar arrumada para festa, fim de tarde',
    prompt:
      'in a cozy home living room decorated for a small party in the late afternoon, warm natural light from a window, a table with a few party decorations, cozy lived-in details',
  },
  varanda: {
    label: 'Varanda com plantas, luz natural',
    prompt:
      'on a bright home balcony with plants in natural daylight, a small wooden table, relaxed weekend atmosphere, cozy lived-in details',
  },
} as const
export type AmbienteId = keyof typeof AMBIENTES

// ---------------------------------------------------------------------------------------------
// Roteiro
// ---------------------------------------------------------------------------------------------

/** Palavras faladas: separa por espaço e ignora o que é só pontuação ("—", "!"). */
export function contarPalavras(texto: string): number {
  return (texto || '')
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w)).length
}

export function segundosFala(palavras: number): number {
  return palavras / PALAVRAS_POR_SEGUNDO
}

export type Checagem = { nivel: 'erro' | 'aviso'; texto: string }

/**
 * Regras do roteiro (manual, 4.3). Erro trava o pedido; aviso só alerta.
 * - fala vazia ou com mais de 12 palavras → erro (não cabe em 5 s e o lip-sync desanda)
 * - algarismo ou unidade abreviada ("220 ml", "6un") → aviso: a apresentadora lê o que está
 *   escrito, e a legenda também. Melhor escrever como ela vai falar ("duzentos e vinte mililitros").
 * - aspas duplas → erro: a fala entra no prompt entre aspas e quebraria o texto.
 */
export function checarFala(texto: string): Checagem[] {
  const out: Checagem[] = []
  const t = (texto || '').trim()
  const n = contarPalavras(t)
  if (n === 0) {
    out.push({ nivel: 'erro', texto: 'Fala vazia.' })
    return out
  }
  if (n > MAX_PALAVRAS_MODULO) {
    out.push({
      nivel: 'erro',
      texto: `${n} palavras — passa de ${MAX_PALAVRAS_MODULO} e não cabe em ${SEGUNDOS_POR_MODULO} s.`,
    })
  }
  if (/["“”]/.test(t)) {
    out.push({ nivel: 'erro', texto: 'Tire as aspas duplas: a fala já entra entre aspas no prompt.' })
  }
  if (/\d/.test(t)) {
    out.push({
      nivel: 'aviso',
      texto: 'Tem algarismo. Escreva como ela vai falar ("seis taças", "duzentos e vinte mililitros") — a legenda usa o mesmo texto.',
    })
  } else if (/\b(ml|cm|mm|kg|un|und|pçs|pcs|w)\b/i.test(t)) {
    out.push({ nivel: 'aviso', texto: 'Unidade abreviada. Escreva por extenso, como ela vai falar.' })
  }
  return out
}

export function checarRoteiro(r: Roteiro): Record<ModuloId, Checagem[]> {
  return {
    hook: checarFala(r.hook),
    carac: checarFala(r.carac),
    det: checarFala(r.det),
    cta: checarFala(r.cta),
  }
}

export function roteiroOk(r: Roteiro): boolean {
  return Object.values(checarRoteiro(r)).every((cs) => cs.every((c) => c.nivel !== 'erro'))
}

/**
 * Modelos de fala para destravar o roteiro. `{p}` vira a "chamada" do produto e só aparece
 * como objeto direto ou depois de "por", para não esbarrar em contração ("nessas", "dessas")
 * nem em concordância de gênero/número. São pontos de partida — a Mariana edita. Nenhum
 * promete preço, prazo ou desconto que a tela não conhece.
 */
export const MODELOS_FALA: Record<ModuloId, string[]> = {
  hook: [
    'Ninguém acredita quanto eu paguei por {p}!',
    'Olha {p} que eu achei pra festa!',
    'Minha festa mudou de nível com isso aqui, olha!',
  ],
  carac: [
    'Olha só {p} de perto, tudo muito bem feito.',
    'Vem bem embalado, chega rapidinho e é muito fácil de usar.',
    'Leve, resistente e com acabamento de loja cara.',
  ],
  det: [
    'Repara no brilho e no acabamento, tudo muito caprichado.',
    'Olha esse detalhe de perto, perfeito pra foto da festa.',
    'O acabamento é impecável, dá pra ver cada detalhe.',
  ],
  cta: [
    'Corre no link do Mercado Livre e garante o seu hoje!',
    'Clica no link e leva o seu antes que acabe!',
    'Tá no Mercado Livre, clica no link e garante já!',
  ],
}

/** Troca `{p}` pela chamada; sem chamada, "esse produto". */
export function aplicarModelo(modelo: string, chamada: string): string {
  const c = (chamada || '').trim() || 'esse produto'
  return modelo.replace(/\{p\}/g, c)
}

// ---------------------------------------------------------------------------------------------
// Insumos do roteiro — de onde vem cada fala
// ---------------------------------------------------------------------------------------------
//
// Decisão da Mariana (spec PESQUISA_PARA_ROTEIRO.md, 23/09/2026): o roteiro não sai de modelo
// genérico. Sai da pesquisa da Fábrica de Anúncio e do próprio anúncio (ficha, avaliações,
// perguntas, campeões), que o bridge lê em `/fabrica-videos/insumos`. Aqui a gente só ordena
// esses dados em "pistas" por módulo, cada uma com fonte e com a marca de dado real ou hipótese.
// Quem escreve a fala continua sendo a Mariana (ou o Claude na skill, com a fonte ao lado).

export type FonteInsumo = 'pesquisa' | 'avaliacoes' | 'perguntas' | 'ficha' | 'campeoes' | 'anuncio'

export type Pista = {
  texto: string
  fonte: FonteInsumo
  /** real = como veio da API ou da pesquisa; hipotese = agrupamento/leitura nossa. */
  tipo: 'real' | 'hipotese'
  detalhe?: string
}

export type ResumoInsumos = {
  mlb: string
  geradoEm: string
  fonte: string
  pesquisaExiste: boolean
  pesquisaData: string
  pesquisaAviso: string
  avaliacoesTotal: number
  avaliacoesMedia: number | null
  perguntasTotal: number
  campeoes: number
  avisos: string[]
  pistas: Record<ModuloId, Pista[]>
}

const ROTULO_FONTE: Record<FonteInsumo, string> = {
  pesquisa: 'pesquisa da Fábrica de Anúncio',
  avaliacoes: 'avaliações do anúncio',
  perguntas: 'perguntas do anúncio',
  ficha: 'ficha do anúncio',
  campeoes: 'campeões do catálogo',
  anuncio: 'anúncio',
}

export function rotuloFonte(f: FonteInsumo): string {
  return ROTULO_FONTE[f]
}

const brlCurto = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

function textoDe(x: unknown): string {
  if (typeof x === 'string') return x.trim()
  if (x && typeof x === 'object' && 'texto' in x) return String((x as { texto: unknown }).texto || '').trim()
  return ''
}

function tipoDe(x: unknown): 'real' | 'hipotese' {
  if (x && typeof x === 'object' && 'tipo' in x) {
    return String((x as { tipo: unknown }).tipo || '') === 'hipotese' ? 'hipotese' : 'real'
  }
  return 'real'
}

/**
 * Ordena os insumos em pistas por módulo. Regra do spec:
 *   hook  = objeção nº 1 ou ocasião, virada em curiosidade
 *   carac = diferencial contra os campeões (e a ficha: quantidade, material, medida)
 *   det   = a prova que derruba a objeção (o que encanta nas avaliações)
 *   cta   = ação no link do ML; preço só se a faixa dos campeões sustentar
 * Não escreve fala nenhuma: lista o dado com a fonte para a fala ser escrita em cima dele.
 */
export function pistasPorModulo(r: RoteiroInsumos | null | undefined): Record<ModuloId, Pista[]> {
  const out: Record<ModuloId, Pista[]> = { hook: [], carac: [], det: [], cta: [] }
  if (!r || !r.ok) return out
  const p = r.pesquisa
  const av = r.avaliacoes
  const pg = r.perguntas
  const an = r.anuncio

  // --- hook: objeções e ocasiões da pesquisa, temas das perguntas, o que decepciona
  for (const o of (p?.existe && p.objecoes) || []) {
    const t = textoDe(o)
    if (t) {
      out.hook.push({
        texto: t,
        fonte: 'pesquisa',
        tipo: tipoDe(o),
        detalhe: [o.fonte, o.quantidade ? `${o.quantidade}×` : ''].filter(Boolean).join(' · '),
      })
    }
  }
  for (const o of (p?.existe && p.ocasioes) || []) {
    const t = textoDe(o)
    if (t) out.hook.push({ texto: `Ocasião: ${t}`, fonte: 'pesquisa', tipo: 'real' })
  }
  for (const t of pg?.temas || []) {
    out.hook.push({
      texto: `${t.quantidade} perguntas falam de "${t.tema}"`,
      fonte: 'perguntas',
      tipo: 'hipotese',
      detalhe: t.exemplos[0] || '',
    })
  }
  for (const n of (av?.negativas || []).slice(0, 2)) {
    out.hook.push({ texto: n.texto || n.titulo, fonte: 'avaliacoes', tipo: 'real', detalhe: `nota ${n.nota} · o que decepciona` })
  }

  // --- carac: diferenciais da pesquisa, ficha, campeões
  for (const d of (p?.existe && p.diferenciais) || []) {
    const t = textoDe(d)
    if (t) out.carac.push({ texto: t, fonte: 'pesquisa', tipo: tipoDe(d), detalhe: d.fonte || '' })
  }
  for (const a of (an?.atributos || []).slice(0, 8)) {
    out.carac.push({ texto: `${a.nome}: ${a.valor}`, fonte: 'ficha', tipo: 'real' })
  }
  for (const c of r.campeoes || []) {
    const preco = c.preco != null ? brlCurto(c.preco) : 'preço não informado'
    out.carac.push({
      texto: `Campeão ${c.mlb}: ${preco} · ${c.vendidos.toLocaleString('pt-BR')} vendidos`,
      fonte: 'campeoes',
      tipo: 'real',
      detalhe: c.titulo,
    })
  }

  // --- det: o que encanta (avaliações), elogios da pesquisa, o que quebra a objeção
  for (const e of (p?.existe && p.elogios) || []) {
    const t = textoDe(e)
    if (t) out.det.push({ texto: t, fonte: 'pesquisa', tipo: tipoDe(e), detalhe: e.fonte || '' })
  }
  for (const pz of (av?.positivas || []).slice(0, 4)) {
    out.det.push({ texto: pz.texto || pz.titulo, fonte: 'avaliacoes', tipo: 'real', detalhe: `nota ${pz.nota} · ${pz.curtidas} curtidas` })
  }
  for (const q of (pg?.lista || []).filter((x) => x.resposta).slice(0, 3)) {
    out.det.push({ texto: `${q.pergunta} → ${q.resposta}`, fonte: 'perguntas', tipo: 'real' })
  }

  // --- cta: preço real de hoje e faixa dos campeões; nunca promete o que não está no anúncio
  if (an?.preco != null) {
    out.cta.push({ texto: `Preço no anúncio hoje: ${brlCurto(an.preco)}`, fonte: 'anuncio', tipo: 'real' })
  }
  const fp = p?.existe ? p.faixa_preco : undefined
  if (fp && (fp.min != null || fp.max != null)) {
    const min = fp.min != null ? brlCurto(fp.min) : '?'
    const max = fp.max != null ? brlCurto(fp.max) : '?'
    out.cta.push({ texto: `Faixa dos campeões: ${min} a ${max}`, fonte: 'pesquisa', tipo: 'real' })
  } else if ((r.campeoes || []).some((c) => c.preco != null)) {
    const precos = (r.campeoes || []).map((c) => c.preco).filter((v): v is number => v != null)
    out.cta.push({
      texto: `Campeões do catálogo: ${brlCurto(Math.min(...precos))} a ${brlCurto(Math.max(...precos))}`,
      fonte: 'campeoes',
      tipo: 'real',
    })
  }
  if (av?.media != null && av.total > 0) {
    out.cta.push({ texto: `Nota ${av.media.toLocaleString('pt-BR')} em ${av.total} avaliações`, fonte: 'avaliacoes', tipo: 'real' })
  }
  return out
}

/** O que fica salvo no rascunho e vai no pedido: só o resumo, nunca o payload inteiro. */
export function resumirInsumos(r: RoteiroInsumos | null | undefined): ResumoInsumos | null {
  if (!r || !r.ok) return null
  return {
    mlb: r.mlb,
    geradoEm: r.gerado_em || '',
    fonte: r.fonte || '',
    pesquisaExiste: Boolean(r.pesquisa?.existe),
    pesquisaData: r.pesquisa?.data_pesquisa || '',
    pesquisaAviso: r.pesquisa?.aviso || '',
    avaliacoesTotal: r.avaliacoes?.total || 0,
    avaliacoesMedia: r.avaliacoes?.media ?? null,
    perguntasTotal: r.perguntas?.total || 0,
    campeoes: (r.campeoes || []).length,
    avisos: r.avisos || [],
    pistas: pistasPorModulo(r),
  }
}

// ---------------------------------------------------------------------------------------------
// Elemento
// ---------------------------------------------------------------------------------------------

/** Nome sugerido para o Elemento: minúsculas, sem acento, hífen, até 32 caracteres, cortado
 *  numa palavra inteira. "Taça de Champanhe Sidera 220ml" → "taca-de-champanhe-sidera-220ml". */
export function nomeElementoSugerido(titulo: string): string {
  const base = (titulo || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (base.length <= 32) return base
  const cortado = base.slice(0, 33)
  const ult = cortado.lastIndexOf('-')
  return (ult > 8 ? cortado.slice(0, ult) : base.slice(0, 32)).replace(/-+$/, '')
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function checarElemento(nome: string, id: string): Checagem[] {
  const out: Checagem[] = []
  const n = (nome || '').trim()
  if (!n) out.push({ nivel: 'erro', texto: 'Dê um nome ao Elemento.' })
  else if (n.length > 32) out.push({ nivel: 'erro', texto: 'O Higgsfield aceita até 32 caracteres no nome.' })
  else if (!/^[a-z0-9-]+$/.test(n)) {
    out.push({ nivel: 'erro', texto: 'Use só minúsculas, números e hífen (sem espaço nem acento).' })
  }
  const i = (id || '').trim()
  if (i && !UUID.test(i)) {
    out.push({ nivel: 'erro', texto: 'O ID do Elemento tem o formato 1c21c2a4-74d7-4261-9a1b-fcfa9d032a5b.' })
  }
  if (!i) {
    out.push({
      nivel: 'aviso',
      texto: 'Sem ID: o Claude cria o Elemento com a foto principal do anúncio antes de gerar qualquer coisa.',
    })
  }
  return out
}

export function normalizarMlb(v: string): string {
  const m = /MLB-?(\d{6,})/i.exec((v || '').trim())
  if (m) return `MLB${m[1]}`
  const d = /^(\d{6,})$/.exec((v || '').trim())
  return d ? `MLB${d[1]}` : (v || '').trim().toUpperCase()
}

export function checarProduto(p: Briefing['produto']): Checagem[] {
  const out: Checagem[] = []
  if (!/^MLB\d{6,}$/.test(p.mlb)) out.push({ nivel: 'erro', texto: 'Informe o MLB do anúncio (ex.: MLB5283170855).' })
  if (!p.titulo.trim()) out.push({ nivel: 'erro', texto: 'Informe o título do anúncio.' })
  if (!p.conta) {
    out.push({ nivel: 'aviso', texto: 'Conta não informada — o Claude vai procurar o MLB nas duas contas.' })
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Custo
// ---------------------------------------------------------------------------------------------

export type Custo = {
  creditos: number
  creditosRefazerModulo: number
  usd: number
  brl: number
  brlRefazerModulo: number
}

export function custoVideo(c: Briefing['custo']): Custo {
  const creditos = CREDITOS.cena + 4 * CREDITOS.modulo + CREDITOS.montagem
  const usdCred = PLANOS[c.plano].usdPorCredito
  const fator = c.iof ? 1 + IOF_CARTAO_INTERNACIONAL : 1
  const dolar = Number.isFinite(c.dolar) && c.dolar > 0 ? c.dolar : 0
  const brlPorCredito = usdCred * dolar * fator
  return {
    creditos,
    creditosRefazerModulo: CREDITOS.modulo,
    usd: creditos * usdCred,
    brl: creditos * brlPorCredito,
    brlRefazerModulo: CREDITOS.modulo * brlPorCredito,
  }
}

// ---------------------------------------------------------------------------------------------
// Pedido para o Claude
// ---------------------------------------------------------------------------------------------

export function briefingVazio(): Briefing {
  return {
    produto: { mlb: '', titulo: '', conta: '', link: '', thumbnail: '' },
    elemento: { nome: '', id: '' },
    chamada: '',
    roteiro: { hook: '', carac: '', det: '', cta: '' },
    cena: {
      apresentadora: 'mulher_32',
      ambiente: 'cozinha',
      partes: '',
      detalhesVisuais: '',
      embalagem: '',
      ajuste: '',
    },
    custo: { plano: 'ultra_mensal', dolar: DOLAR_PADRAO, iof: false },
    insumos: null,
    projetoId: '',
  }
}

/** Pronto para gerar no BI: produto e roteiro válidos. (Elemento não existe na API paga.) */
export function briefingPronto(b: Briefing): boolean {
  return checarProduto(b.produto).every((c) => c.nivel !== 'erro') && roteiroOk(b.roteiro)
}

/** Pronto para o caminho alternativo pelo Claude (exige o Elemento do conector). */
export function pedidoPronto(b: Briefing): boolean {
  return briefingPronto(b) && checarElemento(b.elemento.nome, b.elemento.id).every((c) => c.nivel !== 'erro')
}

// ---------------------------------------------------------------------------------------------
// Geração no BI (serviço 8105)
// ---------------------------------------------------------------------------------------------

export type EtapaGeracao = 'roteiro' | 'cena' | 'modulos' | 'montagem' | 'pronto'

export const ROTULO_ETAPA: Record<EtapaGeracao, string> = {
  roteiro: 'Roteiro a aprovar',
  cena: 'Cena base',
  modulos: '4 módulos',
  montagem: 'Montagem',
  pronto: 'Vídeo pronto',
}

export const fmtUsdBr = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })
export const fmtBrlBr = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
export const fmtCred = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} cr`

/** Linha curta de custo: "45 cr · US$ 2,50 · R$ 13,40". brl opcional. */
export function linhaCusto(p: { credits: number; usd: number; brl?: number }): string {
  const partes = [fmtCred(p.credits), fmtUsdBr(p.usd)]
  if (p.brl != null) partes.push(fmtBrlBr(p.brl))
  return partes.join(' · ')
}

/** Peças que ainda estão sendo geradas — a tela consulta o serviço enquanto houver alguma. */
export function emGeracao(p: { cena: { status: string }; modulos: Record<string, { status: string }>; montagem: { status: string } }): boolean {
  if (p.cena.status === 'gerando' || p.cena.status === 'fila') return true
  if (Object.values(p.modulos).some((m) => m.status === 'gerando' || m.status === 'fila')) return true
  return p.montagem.status === 'montando'
}

function linha(rotulo: string, valor: string): string {
  const v = (valor || '').trim()
  return `${rotulo}: ${v || 'não informado'}`
}

const fmtBrl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtUsd = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })

const MODULO_NOME: Record<ModuloId, string> = { hook: 'Hook', carac: 'Característica', det: 'Close de detalhes', cta: 'CTA' }

/** Bloco INSUMOS do pedido: até 4 pistas por módulo, cada uma com [real]/[hipótese] e a fonte. */
export function linhasInsumosPedido(ins: ResumoInsumos | null | undefined): string[] {
  if (!ins) {
    return [
      'Não carregados (bridge indisponível ou anúncio não escolhido). Antes de propor as falas, leia pela',
      'skill mercado-livre-api: /items/{MLB}, /items/{MLB}/description, /reviews/item/{MLB} e',
      '/questions/search?item={MLB}&api_version=4. Sem isso, não proponha roteiro.',
    ]
  }
  const out: string[] = []
  out.push(
    ins.pesquisaExiste
      ? `Pesquisa da Fábrica de Anúncio: existe (${ins.pesquisaData || 'data não informada'})${ins.pesquisaAviso ? ` — ${ins.pesquisaAviso}` : ''}`
      : 'Pesquisa da Fábrica de Anúncio: NÃO existe para este MLB (rodar /fabricadeanuncio). Abaixo, só o próprio anúncio.',
  )
  const media = ins.avaliacoesMedia != null ? ins.avaliacoesMedia.toLocaleString('pt-BR') : '—'
  out.push(
    `Lido pelo BI em ${ins.geradoEm ? ins.geradoEm.slice(0, 10) : 'data não informada'}: ${ins.avaliacoesTotal} avaliações (nota ${media}), ${ins.perguntasTotal} perguntas, ${ins.campeoes} campeões do catálogo.`,
  )
  for (const id of ['hook', 'carac', 'det', 'cta'] as ModuloId[]) {
    const ps = ins.pistas[id] || []
    out.push(`${MODULO_NOME[id]}:`)
    if (ps.length === 0) {
      out.push('  - sem insumo')
      continue
    }
    for (const p of ps.slice(0, 4)) {
      out.push(`  - [${p.tipo === 'real' ? 'real' : 'hipótese'} · ${rotuloFonte(p.fonte)}] ${p.texto}${p.detalhe ? ` (${p.detalhe})` : ''}`)
    }
  }
  if (ins.avisos.length) out.push(`Avisos do bridge: ${ins.avisos.join(' | ')}`)
  out.push('Regra: cada fala proposta cita a pista que a sustenta. Nada que não esteja acima nem no anúncio.')
  return out
}

/**
 * O texto que a Mariana cola no Claude. Começa com `/fabricadevideos` para a skill carregar e
 * traz tudo que a skill precisa para não perguntar de novo. O roteiro vai como PROPOSTA da
 * Etapa 1: o Claude mostra e espera "aprovar roteiro" — a cadeia de aprovação do manual
 * continua valendo, só começa com o texto já revisado.
 */
export function montarPedido(b: Briefing, agora: Date = new Date()): string {
  const custo = custoVideo(b.custo)
  const ap = APRESENTADORAS[b.cena.apresentadora]
  const amb = AMBIENTES[b.cena.ambiente]
  const data = agora.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
  const conta = b.produto.conta || 'não informada (procure nas duas contas)'
  const elementoId = b.elemento.id.trim()
    ? b.elemento.id.trim()
    : 'ainda não existe — crie com a foto principal do anúncio antes de qualquer geração'

  const falas = MODULOS.map(
    (m) => `${m.n}. ${m.nome} (${contarPalavras(b.roteiro[m.id])} palavras): ${b.roteiro[m.id].trim()}`,
  ).join('\n')

  return [
    '/fabricadevideos',
    '',
    `Briefing montado na tela Fábrica de Vídeos do Fiesta BI em ${data}.`,
    '',
    'PRODUTO',
    linha('Anúncio', `${b.produto.mlb} — ${b.produto.titulo.trim()}`),
    linha('Conta do Mercado Livre', conta),
    linha('Link', b.produto.link),
    '',
    'ELEMENTO NO HIGGSFIELD',
    linha('Nome', b.elemento.nome),
    linha('ID', elementoId),
    '',
    'ROTEIRO (proposta da Etapa 1: mostre e espere eu escrever "aprovar roteiro")',
    falas,
    '',
    'INSUMOS DO ROTEIRO (de onde cada fala tem que sair)',
    ...linhasInsumosPedido(b.insumos),
    '',
    'CENA',
    linha('Apresentadora', ap.label),
    linha('Ambiente', amb.label),
    linha('Partes que ela mostra no módulo 2', b.cena.partes),
    linha('Detalhes do close no módulo 3', b.cena.detalhesVisuais),
    linha('Caixa/kit na mesa no CTA', b.cena.embalagem),
    linha('Ajuste pedido na cena', b.cena.ajuste),
    '',
    'ORÇAMENTO',
    `Estimativa: ${custo.creditos} créditos (≈ ${fmtUsd(custo.usd)} ≈ ${fmtBrl(custo.brl)} no ${PLANOS[b.custo.plano].label}).`,
    `Refazer um módulo: ${custo.creditosRefazerModulo} créditos. Confira o saldo (balance) antes de começar e me avise se não der.`,
    '',
    'Siga as 4 etapas da skill com aprovação obrigatória entre elas. No fim me entregue o link do MP4 1080×1920, o custo real (transactions) e o roteiro final.',
  ].join('\n')
}

// ---------------------------------------------------------------------------------------------
// Rascunho local
// ---------------------------------------------------------------------------------------------

export const CHAVE_RASCUNHO = 'fiesta-bi:fabrica-videos:rascunho'

/** Lê um rascunho salvo, completando campos que versões antigas não tinham. Qualquer coisa
 *  estranha → briefing vazio (nunca quebra a tela). */
export function lerRascunho(raw: string | null): Briefing {
  const base = briefingVazio()
  if (!raw) return base
  try {
    const x = JSON.parse(raw) as Partial<Briefing>
    const apresentadora =
      x.cena?.apresentadora && x.cena.apresentadora in APRESENTADORAS ? x.cena.apresentadora : base.cena.apresentadora
    const ambiente = x.cena?.ambiente && x.cena.ambiente in AMBIENTES ? x.cena.ambiente : base.cena.ambiente
    const plano = x.custo?.plano && x.custo.plano in PLANOS ? x.custo.plano : base.custo.plano
    return {
      produto: { ...base.produto, ...(x.produto || {}) },
      elemento: { ...base.elemento, ...(x.elemento || {}) },
      chamada: typeof x.chamada === 'string' ? x.chamada : '',
      roteiro: { ...base.roteiro, ...(x.roteiro || {}) },
      cena: { ...base.cena, ...(x.cena || {}), apresentadora, ambiente },
      custo: {
        plano,
        dolar: Number(x.custo?.dolar) > 0 ? Number(x.custo?.dolar) : base.custo.dolar,
        iof: Boolean(x.custo?.iof),
      },
      insumos:
        x.insumos && typeof x.insumos === 'object' && typeof x.insumos.mlb === 'string' && x.insumos.pistas
          ? {
              ...x.insumos,
              avisos: Array.isArray(x.insumos.avisos) ? x.insumos.avisos : [],
              pistas: {
                hook: x.insumos.pistas.hook || [],
                carac: x.insumos.pistas.carac || [],
                det: x.insumos.pistas.det || [],
                cta: x.insumos.pistas.cta || [],
              },
            }
          : null,
      projetoId: typeof x.projetoId === 'string' && /^[a-f0-9]{12}$/.test(x.projetoId) ? x.projetoId : '',
    }
  } catch {
    return base
  }
}
